import { Check, Loader2 } from "lucide-react";

import { cn } from "@/shared/lib/cn";

/** Шаги pipeline'а из orchestrator.py (по порядку и с теми же лейблами). */
const PIPELINE_STEPS: readonly { id: string; label: string }[] = [
  { id: "prepare_seed", label: "Сбор стартовых фраз" },
  { id: "collect_top", label: "Сбор частот" },
  { id: "clean", label: "Очистка мусора" },
  { id: "cluster", label: "Кластеризация" },
  { id: "collect_dynamics", label: "Сбор динамики" },
  { id: "compute_heat", label: "Метрики горячести" },
];

export interface PipelineStepsProps {
  /** Текущий step id из orchestrator'а. `null` пока не стартовало или после финала. */
  currentStep: string | null;
  /** 0..100. */
  progress: number;
  /** `true` если шаги пройдены — рисуем все галочками. */
  done?: boolean;
}

export function PipelineSteps({
  currentStep,
  progress,
  done = false,
}: PipelineStepsProps): JSX.Element {
  const currentIdx = currentStep
    ? PIPELINE_STEPS.findIndex((s) => s.id === currentStep)
    : -1;

  return (
    <div className="glass-card p-5">
      <div className="mb-4 flex items-baseline justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Конвейер
        </div>
        <div className="font-mono text-sm text-cyan">{progress}%</div>
      </div>

      {/* progress-bar */}
      <div className="mb-5 h-2 w-full overflow-hidden rounded-full bg-white/[0.06]">
        <div
          className="h-full bg-gradient-to-r from-purple to-cyan transition-all"
          style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
        />
      </div>

      <ol className="space-y-2">
        {PIPELINE_STEPS.map((step, idx) => {
          const isDone = done || (currentIdx >= 0 && idx < currentIdx);
          const isActive = !done && idx === currentIdx;
          return (
            <li
              key={step.id}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                isActive && "bg-white/[0.04]",
              )}
            >
              <div
                className={cn(
                  "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold",
                  isDone && "bg-gradient-primary text-white shadow-glow-purple",
                  isActive && "bg-gradient-primary text-white shadow-glow-purple",
                  !isDone && !isActive &&
                    "border border-white/15 bg-white/[0.04] text-muted-foreground",
                )}
              >
                {isDone ? (
                  <Check className="h-3.5 w-3.5" />
                ) : isActive ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <span>{idx + 1}</span>
                )}
              </div>
              <span
                className={cn(
                  "flex-1",
                  isActive && "text-white",
                  !isActive && !isDone && "text-muted-foreground",
                  isDone && "text-foreground",
                )}
              >
                {step.label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
